/* eslint-disable jsx-a11y/label-has-for */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Script from 'next/script';
import Loader from 'src/components/Loader';
import { useIntl } from 'react-intl';

import theme from 'src/styles/components/QrScannerPlugin.module.scss';

const SCANNER_TYPES = {
    CAMERA: 'camera',
    FILE: 'file'
};

const Html5QrcodeScannerPlugin = ({ fps, qrbox, onQrcodeScanned, onQrcodeError }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [activeScanner, setActiveScanner] = useState(null);
    const [camera, setCamera] = useState(null);
    const [type, setType] = useState(null);

    const intl = useIntl();

    const detectCamera = useCallback(() => {
        // eslint-disable-next-line no-undef
        Html5Qrcode.getCameras().then((devices) => {
            if (devices && devices.length) {
                const cameraDevice = devices.find((device) => device.label.includes('back')
                        || device.label.includes('Back')
                        || device.label.includes('rear')
                        || device.label.includes('environment'))
                    || (devices.length > 1 && devices[1])
                    || devices[0];

                if (cameraDevice) {
                    setCamera(cameraDevice.id);
                    setType(SCANNER_TYPES.CAMERA);
                } else {
                    setCamera(null);
                    setType(SCANNER_TYPES.FILE);
                }
            }

            setIsInitialized(true);
        }).catch(() => {
            setIsInitialized(true);
            setCamera(null);
            setType(SCANNER_TYPES.FILE);
        });
    }, []);

    useEffect(() => {
        if (!isInitialized || activeScanner) {
            return;
        }

        if (type === SCANNER_TYPES.CAMERA && camera) {
            // eslint-disable-next-line no-undef
            const html5QrCode = new Html5Qrcode('qr-code-reader');

            const qconfig = { fps, qrbox };
            html5QrCode.start({ deviceId: { exact: camera } }, qconfig, onQrcodeScanned);

            setActiveScanner(html5QrCode);
        } else if (type === SCANNER_TYPES.FILE) {
            // eslint-disable-next-line no-undef
            const html5QrCode = new Html5Qrcode('upload-photo');

            setActiveScanner(html5QrCode);
        }
    }, [camera, isInitialized, type]);

    useEffect(() => {
        if (typeof Html5Qrcode !== 'undefined') {
            detectCamera();

            if (typeof ZXing !== 'undefined') {
                // eslint-disable-next-line no-undef
                const oldFunc = ZXing.HTMLCanvasElementLuminanceSource.toGrayscaleBuffer;
                let inverterToggle = true;
                // eslint-disable-next-line no-undef
                ZXing.HTMLCanvasElementLuminanceSource.toGrayscaleBuffer = function (imagebuffer, width, height) {
                    const grayscaleBuffer = oldFunc(imagebuffer, width, height);
                    const len = grayscaleBuffer.length;
                    inverterToggle = !inverterToggle;
                    if (inverterToggle) {
                        // eslint-disable-next-line no-plusplus
                        for (let i = 0; i < len; i++) {
                            grayscaleBuffer[i] = 0xFF - grayscaleBuffer[i];
                        }
                    }
                    return grayscaleBuffer;
                };
            }
        }
    }, []);

    useEffect(() => () => {
        if (activeScanner) {
            activeScanner.stop().catch((err) => {
                // eslint-disable-next-line no-console
                console.log(err);
            });
        }
    }, [activeScanner]);

    const handleFileChanged = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        activeScanner.scanFile(file, true)
            .then((result) => {
                onQrcodeScanned(result);

                activeScanner.stop();
            })
            .catch(() => {
                onQrcodeError('App.qrCodeNotFound');
            });
    }, [activeScanner]);

    return (
        <div className={theme.container}>
            <Script
              src="/_next/static/html5-qrcode.min.js"
              onLoad={detectCamera}
            />
            {!isInitialized && (
                <div className={theme.loaderContainer}>
                    <Loader />
                </div>
            )}
            {type === SCANNER_TYPES.CAMERA && (
                <div className={theme.cameraContainer}>
                    <div id="qr-code-reader" />
                </div>
            )}
            {type === SCANNER_TYPES.FILE && (
                <div className={theme.fileContent}>
                    <input
                      className={theme.qrScannerInput}
                      type="file"
                      name="photo"
                      id="upload-photo"
                      onChange={handleFileChanged}
                      accept="image/*"
                    />
                    <label
                      className={theme.qrScannerLabel}
                      htmlFor="upload-photo"
                    >
                        {intl.formatMessage({ id: 'App.clickToScan' })}
                    </label>
                </div>
            )}
        </div>
    );
};

Html5QrcodeScannerPlugin.propTypes = {
    fps: PropTypes.number,
    qrbox: PropTypes.number,
    onQrcodeScanned: PropTypes.func.isRequired,
    onQrcodeError: PropTypes.func.isRequired
};

Html5QrcodeScannerPlugin.defaultProps = {
    fps: 60,
    qrbox: 250
};

export default Html5QrcodeScannerPlugin;
